import React from "react";
import ShareButton from "@/components/ShareButton";

// Extracts a YouTube video ID from any of the common URL formats:
//   https://youtube.com/shorts/ABCDEF
//   https://www.youtube.com/watch?v=ABCDEF
//   https://youtu.be/ABCDEF
//   https://www.youtube.com/embed/ABCDEF
function extractYouTubeId(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) {
      return u.pathname.replace(/^\//, "").split("/")[0] || null;
    }
    if (u.hostname.includes("youtube.com")) {
      if (u.pathname.startsWith("/shorts/")) {
        return u.pathname.split("/")[2] || null;
      }
      if (u.pathname.startsWith("/embed/")) {
        return u.pathname.split("/")[2] || null;
      }
      const v = u.searchParams.get("v");
      if (v) return v;
    }
  } catch {
    // Not a valid URL — try a simple regex fallback
    const m = url.match(/([A-Za-z0-9_-]{11})/);
    if (m) return m[1];
  }
  return null;
}

// Home page video section. Shows a YouTube embed above-the-fold when the
// admin has set a video URL in Homepage settings. Hides silently if empty.
export default function HomeVideo({ hp }) {
  const videoId = extractYouTubeId(hp?.video_url);
  if (!videoId) return null;

  const kicker = (hp?.video_kicker || "Watch").trim();
  const caption = (hp?.video_caption || "").trim();
  const src = `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&playsinline=1`;

  return (
    <section
      data-testid="home-video"
      className="bg-paper border-b border-rule"
    >
      <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-10 pb-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          {kicker ? (
            <p className="text-xs uppercase tracking-[0.22em] text-sage">
              {kicker}
            </p>
          ) : <span />}
          <ShareButton
            testId="video-share-btn"
            title={caption || "A glimpse from B. Ruff Writes"}
            excerpt={caption || "A moment from Barbara Ruffing's writing and mission life."}
            label="Share this video"
            url={hp?.video_url || undefined}
            className="inline-flex items-center gap-2 px-4 py-2 text-[11px] tracking-[0.2em] uppercase text-ink-soft hover:text-terracotta transition-colors border border-rule hover:border-terracotta"
          />
        </div>
        <div
          className="relative w-full mx-auto overflow-hidden border border-rule bg-ink max-w-[300px] sm:max-w-[360px] md:max-w-[420px]"
          style={{ aspectRatio: "9 / 16" }}
        >
          <iframe
            src={src}
            title={caption || "B. Ruff Writes video"}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            className="absolute inset-0 w-full h-full"
            loading="lazy"
          />
        </div>
        {caption && (
          <p className="mt-4 font-serif-display text-ink text-lg leading-snug text-center">
            {caption}
          </p>
        )}
      </div>
    </section>
  );
}

